import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ArrowLeft, KeyRound, Mail } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "wouter";

// First step: Request password reset
const requestResetSchema = z.object({
  email: z.string().email("Invalid email address format"),
});

// Second step: Reset password with token
const resetPasswordSchema = z.object({
  token: z.string().min(1, "Reset token is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type RequestResetValues = z.infer<typeof requestResetSchema>;
type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

export default function PasswordResetPage() {
  const { toast } = useToast();
  const [requestSent, setRequestSent] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  
  // Check URL for reset token
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      if (token) {
        setResetToken(token);
      }
    } catch (error) {
      console.error("Error parsing URL params:", error);
    }
  }, []);

  // Request reset form
  const requestForm = useForm<RequestResetValues>({
    resolver: zodResolver(requestResetSchema),
    defaultValues: {
      email: "",
    },
  });

  // Reset password form
  const resetForm = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token: resetToken || "",
      password: "",
      confirmPassword: "",
    },
  });
  
  // Update token field when token is retrieved from URL
  useEffect(() => {
    if (resetToken) {
      resetForm.setValue("token", resetToken);
    }
  }, [resetToken, resetForm]);

  // Request reset mutation
  const requestResetMutation = useMutation({
    mutationFn: async (data: RequestResetValues) => {
      return apiRequest("POST", "/api/auth/request-password-reset", data);
    },
    onSuccess: () => {
      toast({
        title: "Reset Email Sent",
        description: "If an account exists with that email, we've sent a password reset link.",
      });
      setRequestSent(true);
      requestForm.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Request Failed",
        description: error.message || "There was a problem with your request. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (data: ResetPasswordValues) => {
      return apiRequest("POST", "/api/auth/reset-password-with-token", data);
    },
    onSuccess: () => {
      toast({
        title: "Password Reset Successfully",
        description: "Your password has been changed. You can now log in with your new password.",
      });
      resetForm.reset();
      // Redirect to login page after successful reset
      window.location.href = "/auth";
    },
    onError: (error: Error) => {
      toast({
        title: "Reset Failed",
        description: error.message || "There was a problem resetting your password. The token might be invalid or expired.",
        variant: "destructive",
      });
    },
  });

  // Submit handlers
  async function onRequestSubmit(data: RequestResetValues) {
    requestResetMutation.mutate(data);
  }

  async function onResetSubmit(data: ResetPasswordValues) {
    resetPasswordMutation.mutate(data);
  }

  // Display the appropriate form based on the state
  return (
    <div className="container mx-auto max-w-md py-10">
      <Link href="/auth" className="flex items-center text-sm mb-4 hover:underline">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Login
      </Link>
      
      {!resetToken ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Reset Your Password
            </CardTitle>
            <CardDescription>
              Enter your email address and we'll send you a link to reset your password.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {requestSent ? (
              <div className="text-center py-4">
                <Mail className="h-10 w-10 mx-auto mb-2 text-primary" />
                <h3 className="font-medium text-lg mb-2">Check Your Email</h3>
                <p className="text-muted-foreground mb-4">
                  We've sent a password reset link to your email address.
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => setRequestSent(false)}
                  className="mt-2"
                >
                  Send Another Reset Link
                </Button>
              </div>
            ) : (
              <Form {...requestForm}>
                <form onSubmit={requestForm.handleSubmit(onRequestSubmit)} className="space-y-4">
                  <FormField
                    control={requestForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="name@example.com" 
                            type="email" 
                            {...field} 
                            disabled={requestResetMutation.isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={requestResetMutation.isPending}
                  >
                    {requestResetMutation.isPending ? "Sending..." : "Send Reset Link"}
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center">
              <KeyRound className="h-5 w-5 mr-2" />
              Create New Password
            </CardTitle>
            <CardDescription>
              Enter your new password below to complete the reset process.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Form {...resetForm}>
              <form onSubmit={resetForm.handleSubmit(onResetSubmit)} className="space-y-4">
                <FormField
                  control={resetForm.control}
                  name="token"
                  render={({ field }) => (
                    <FormItem className="hidden">
                      <FormControl>
                        <Input type="hidden" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={resetForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter new password" 
                          type="password" 
                          {...field} 
                          disabled={resetPasswordMutation.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={resetForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm New Password</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Confirm new password" 
                          type="password" 
                          {...field} 
                          disabled={resetPasswordMutation.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={resetPasswordMutation.isPending}
                >
                  {resetPasswordMutation.isPending ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}